import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Color from 'color';
import i18n from 'i18n-js';

import FONT_FAMILY from '../../../constants/fontFamily';
import { DateProvider } from '../../../providers';
import FontFamilyHeader from '../../FontFamilyHeader';
import {
  Answer,
  Container,
  Header,
  Question,
  Row,
  Title,
  UsersAnswered,
} from './styles';

function calculateUsersLeft(questions, index) {
  if (index === 0) return 0;

  let total = 0;
  const lastQuestion = questions?.[index - 1];
  if (lastQuestion?.answer1?.correct) {
    total += lastQuestion?.answer1?.users?.length;
  }
  if (lastQuestion?.answer2?.correct) {
    total += lastQuestion?.answer2?.users?.length;
  }
  if (lastQuestion?.answer3?.correct) {
    total += lastQuestion?.answer3?.users?.length;
  }
  if (lastQuestion?.answer4?.correct) {
    total += lastQuestion?.answer4?.users?.length;
  }

  return total;
}

function QuizSimple({ data, styles }) {
  const [, refresh] = useState(Date.now());
  const {
    answering,
    chatInitializedUntil,
    index,
    questions,
    showCorrectAnswers,
    twitchIntegrated,
    winners,
  } = data;
  const { answers, question, timer } = styles || {};
  const usersLeft = calculateUsersLeft(questions, index);

  const questionFontFamily = FONT_FAMILY[styles?.question?.font || 'notoSans'];
  const answersFontFamily = FONT_FAMILY[styles?.answers?.font || 'notoSans'];
  const timerfontFamily = FONT_FAMILY[styles?.timer?.font || 'notoSans'];

  useEffect(() => {
    const interval = setInterval(() => refresh(Date.now()), 1000);
    if (!answering) clearInterval(interval);
    return () => {
      clearInterval(interval);
    };
  }, [answering]);

  const getAnsweringTime = () => {
    const [minutes, seconds] = DateProvider.getTimeUntil(chatInitializedUntil);

    return `${minutes}:${seconds}`;
  };

  return (
    <>
      <FontFamilyHeader family={questionFontFamily} />
      <FontFamilyHeader family={answersFontFamily} />
      <FontFamilyHeader family={timerfontFamily} />
      <Container>
        {!winners && (
          <>
            {twitchIntegrated && (
              <Header>
                <Title
                  style={{
                    color: timer?.color,
                    fontFamily: timerfontFamily?.family,
                    fontSize: timer?.size,
                    letterSpacing: timer?.letterSpacing,
                    lineHeight: timer?.size ? `${timer?.size}px` : undefined,
                  }}
                >
                  {!!usersLeft &&
                    `${i18n.t(
                      'screens.builder.build.quiz.usersLeft'
                    )}: ${usersLeft}`}
                </Title>
                <Title
                  style={{
                    color: timer?.color,
                    fontFamily: timerfontFamily?.family,
                    fontSize: timer?.size,
                    letterSpacing: timer?.letterSpacing,
                    lineHeight: timer?.size ? `${timer?.size}px` : undefined,
                  }}
                >
                  {getAnsweringTime()}
                </Title>
              </Header>
            )}
            <Question
              style={{
                background: `radial-gradient(circle, ${
                  question?.background
                } 30%, ${Color(question?.background).darken(0.5)} 100%)`,
                border: `4px solid ${question?.border}`,
                color: question?.color,
                fontFamily: questionFontFamily?.family,
                fontSize: question?.size,
                letterSpacing: question?.letterSpacing,
                lineHeight: question?.size ? `${question?.size}px` : undefined,
              }}
            >
              {questions?.[index]?.title}
            </Question>
            <Row>
              <Answer
                style={{
                  background: `radial-gradient(circle, ${
                    showCorrectAnswers && questions?.[index]?.answer1?.correct
                      ? 'rgba(0,255, 0, 1)'
                      : answers?.background
                  } 30%, ${Color(
                    showCorrectAnswers && questions?.[index]?.answer1?.correct
                      ? 'rgba(0,255, 0, 1)'
                      : answers?.background
                  ).darken(0.5)} 100%)`,
                  border: `4px solid ${answers?.border}`,
                  color: answers?.color,
                  fontFamily: answersFontFamily?.family,
                  fontSize: answers?.size,
                  letterSpacing: answers?.letterSpacing,
                  lineHeight: answers?.size ? `${answers?.size}px` : undefined,
                }}
              >
                <span>A: </span>
                {questions?.[index]?.answer1?.text}
                {twitchIntegrated && !answering && (
                  <UsersAnswered>
                    {i18n.t('screens.builder.build.quiz.total')}:{' '}
                    {questions?.[index]?.answer1?.users?.length}
                  </UsersAnswered>
                )}
              </Answer>
              <Answer
                style={{
                  background: `radial-gradient(circle, ${
                    showCorrectAnswers && questions?.[index]?.answer2?.correct
                      ? 'rgba(0,255, 0, 1)'
                      : answers?.background
                  } 30%, ${Color(
                    showCorrectAnswers && questions?.[index]?.answer2?.correct
                      ? 'rgba(0,255, 0, 1)'
                      : answers?.background
                  ).darken(0.5)} 100%)`,
                  border: `4px solid ${answers?.border}`,
                  color: answers?.color,
                  fontFamily: answersFontFamily?.family,
                  fontSize: answers?.size,
                  letterSpacing: answers?.letterSpacing,
                  lineHeight: answers?.size ? `${answers?.size}px` : undefined,
                }}
              >
                <span>B: </span>
                {questions?.[index]?.answer2?.text}
                {twitchIntegrated && !answering && (
                  <UsersAnswered>
                    {i18n.t('screens.builder.build.quiz.total')}:{' '}
                    {questions?.[index]?.answer2?.users?.length}
                  </UsersAnswered>
                )}
              </Answer>
            </Row>
            <Row>
              <Answer
                style={{
                  background: `radial-gradient(circle, ${
                    showCorrectAnswers && questions?.[index]?.answer3?.correct
                      ? 'rgba(0,255, 0, 1)'
                      : answers?.background
                  } 30%, ${Color(
                    showCorrectAnswers && questions?.[index]?.answer3?.correct
                      ? 'rgba(0,255, 0, 1)'
                      : answers?.background
                  ).darken(0.5)} 100%)`,
                  border: `4px solid ${answers?.border}`,
                  color: answers?.color,
                  fontFamily: answersFontFamily?.family,
                  fontSize: answers?.size,
                  letterSpacing: answers?.letterSpacing,
                  lineHeight: answers?.size ? `${answers?.size}px` : undefined,
                }}
              >
                <span>C: </span>
                {questions?.[index]?.answer3?.text}
                {twitchIntegrated && !answering && (
                  <UsersAnswered>
                    {i18n.t('screens.builder.build.quiz.total')}:{' '}
                    {questions?.[index]?.answer3?.users?.length}
                  </UsersAnswered>
                )}
              </Answer>
              <Answer
                style={{
                  background: `radial-gradient(circle, ${
                    showCorrectAnswers && questions?.[index]?.answer4?.correct
                      ? 'rgba(0,255, 0, 1)'
                      : answers?.background
                  } 30%, ${Color(
                    showCorrectAnswers && questions?.[index]?.answer4?.correct
                      ? 'rgba(0,255, 0, 1)'
                      : answers?.background
                  ).darken(0.5)} 100%)`,
                  border: `4px solid ${answers?.border}`,
                  color: answers?.color,
                  fontFamily: answersFontFamily?.family,
                  fontSize: answers?.size,
                  letterSpacing: answers?.letterSpacing,
                  lineHeight: answers?.size ? `${answers?.size}px` : undefined,
                }}
              >
                <span>D: </span>
                {questions?.[index]?.answer4?.text}
                {twitchIntegrated && !answering && (
                  <UsersAnswered>
                    {i18n.t('screens.builder.build.quiz.total')}:{' '}
                    {questions?.[index]?.answer4?.users?.length}
                  </UsersAnswered>
                )}
              </Answer>
            </Row>
          </>
        )}
        {!!winners && (
          <Question
            style={{
              background: question?.background,
              border: `4px solid ${question?.border}`,
              color: question?.color,
              fontFamily: questionFontFamily?.family,
              fontSize: question?.size,
              letterSpacing: question?.letterSpacing,
              lineHeight: question?.size ? `${question?.size}px` : undefined,
            }}
          >
            {i18n.t('screens.builder.build.quiz.winners')}: {winners.join(', ')}
          </Question>
        )}
      </Container>
    </>
  );
}

QuizSimple.propTypes = {
  data: PropTypes.shape({
    answering: PropTypes.bool,
    chatInitializedUntil: PropTypes.number,
    index: PropTypes.number,
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        answer1: PropTypes.shape({
          correct: PropTypes.bool,
          text: PropTypes.string,
          users: PropTypes.arrayOf(PropTypes.string),
        }),
        answer2: PropTypes.shape({
          correct: PropTypes.bool,
          text: PropTypes.string,
          users: PropTypes.arrayOf(PropTypes.string),
        }),
        answer3: PropTypes.shape({
          correct: PropTypes.bool,
          text: PropTypes.string,
          users: PropTypes.arrayOf(PropTypes.string),
        }),
        answer4: PropTypes.shape({
          correct: PropTypes.bool,
          text: PropTypes.string,
          users: PropTypes.arrayOf(PropTypes.string),
        }),
        title: PropTypes.string,
      })
    ),
    showCorrectAnswers: PropTypes.bool,
    twitchIntegrated: PropTypes.bool,
    winners: PropTypes.arrayOf(PropTypes.string),
  }),
  styles: PropTypes.shape({
    answers: PropTypes.shape({
      background: PropTypes.string,
      border: PropTypes.string,
      color: PropTypes.string,
      font: PropTypes.string,
      letterSpacing: PropTypes.number,
      size: PropTypes.number,
    }),
    question: PropTypes.shape({
      background: PropTypes.string,
      border: PropTypes.string,
      color: PropTypes.string,
      font: PropTypes.string,
      letterSpacing: PropTypes.number,
      size: PropTypes.number,
    }),
    timer: PropTypes.shape({
      color: PropTypes.string,
      font: PropTypes.string,
      letterSpacing: PropTypes.number,
      size: PropTypes.number,
    }),
  }),
};

QuizSimple.defaultProps = {
  data: {
    answering: true,
    chatInitializedUntil: null,
    index: 0,
    questions: [],
    twitchIntegrated: false,
    winners: null,
  },
  styles: {
    answers: {
      background: '#201683',
      border: '#bfc2c7',
      color: '#ffffff',
      font: 'notoSans',
      letterSpacing: 0,
      size: 24,
    },
    question: {
      background: '#201683',
      border: '#bfc2c7',
      color: '#ffffff',
      font: 'notoSans',
      letterSpacing: 0,
      size: 36,
    },
    timer: {
      color: '#FFFFFF',
      font: 'notoSans',
      letterSpacing: 0,
      size: 30,
    },
  },
};

export default QuizSimple;
